<script setup lang="ts">
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/ru.js";
import { computed, ref, watch } from "vue";
import type { TicketDateOption } from "@/types";

interface CalendarCell {
  id: string;
  day: string;
  date: Dayjs;
  option?: TicketDateOption;
}

const props = defineProps<{
  dates: TicketDateOption[];
  selectedDateId: string;
  formatPrice: (value: number) => string;
}>();

const emit = defineEmits<{
  close: [];
  select: [dateId: string];
}>();

const weekdays = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
const pendingDateId = ref(props.selectedDateId);
const visibleMonth = ref(getInitialVisibleMonth());
const slideDirection = ref<"next" | "previous">("next");

const today = computed(() => dayjs().startOf("day"));
const dateMap = computed(
  () => new Map(props.dates.map((date) => [date.id, date])),
);
const maxDate = computed(() => {
  const lastDate = props.dates[props.dates.length - 1]?.id;

  return lastDate ? dayjs(lastDate).startOf("day") : today.value;
});

const calendarCells = computed<Array<CalendarCell | null>>(() => {
  const firstDay = visibleMonth.value.startOf("month");
  const leadingEmptyCells = getMondayBasedWeekday(firstDay);
  const daysInMonth = visibleMonth.value.daysInMonth();
  const cells: Array<CalendarCell | null> = Array.from(
    { length: leadingEmptyCells },
    () => null,
  );

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = firstDay.date(day);
    const id = date.format("YYYY-MM-DD");

    cells.push({
      id,
      day: String(day),
      date,
      option: dateMap.value.get(id),
    });
  }

  while (cells.length < 42) {
    cells.push(null);
  }

  return cells;
});

const pendingDate = computed(() => {
  const date = dateMap.value.get(pendingDateId.value);

  if (date) {
    return date;
  }

  return props.dates.find((item) => isDateSelectable(dayjs(item.id))) ?? null;
});

const isCurrentSelection = computed(
  () => pendingDateId.value === props.selectedDateId,
);

const actionLabel = computed(() => {
  if (!pendingDate.value || isCurrentSelection.value) {
    return "Отмена";
  }

  return `Выбрать ${pendingDate.value.label}`;
});

const canGoPreviousMonth = computed(() =>
  isMonthInAvailableRange(visibleMonth.value.subtract(1, "month")),
);
const canGoNextMonth = computed(() =>
  isMonthInAvailableRange(visibleMonth.value.add(1, "month")),
);
const visibleMonthKey = computed(() => visibleMonth.value.format("YYYY-MM"));
const monthTransitionName = computed(() =>
  slideDirection.value === "previous"
    ? "ticket-calendar-slide-prev"
    : "ticket-calendar-slide-next",
);

watch(
  () => props.selectedDateId,
  () => {
    const nextVisibleMonth = getInitialVisibleMonth();

    pendingDateId.value = props.selectedDateId;
    slideDirection.value = nextVisibleMonth.isBefore(visibleMonth.value, "month")
      ? "previous"
      : "next";
    visibleMonth.value = nextVisibleMonth;
  },
);

function getInitialVisibleMonth() {
  const selectedDate = dayjs(props.selectedDateId);

  if (selectedDate.isValid()) {
    return selectedDate.locale("ru").startOf("month");
  }

  return dayjs().locale("ru").startOf("month");
}

function getMondayBasedWeekday(date: Dayjs) {
  return (date.day() + 6) % 7;
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatMonth(date: Dayjs) {
  return capitalize(date.locale("ru").format("MMMM"));
}

function isWeekendCell(cell: CalendarCell) {
  const day = cell.date.day();

  return day === 0 || day === 6;
}

function isDateSelectable(date: Dayjs) {
  const dateId = date.format("YYYY-MM-DD");
  const option = dateMap.value.get(dateId);

  return (
    option?.price !== null &&
    option?.price !== undefined &&
    !date.isBefore(today.value, "day") &&
    !date.isAfter(maxDate.value, "day")
  );
}

function isMonthInAvailableRange(month: Dayjs) {
  return (
    !month.endOf("month").isBefore(today.value, "day") &&
    !month.startOf("month").isAfter(maxDate.value, "day")
  );
}

function selectDate(cell: CalendarCell) {
  if (!isDateSelectable(cell.date)) {
    return;
  }

  pendingDateId.value = cell.id;
}

function goToPreviousMonth() {
  if (!canGoPreviousMonth.value) {
    return;
  }

  slideDirection.value = "previous";
  visibleMonth.value = visibleMonth.value.subtract(1, "month");
}

function goToNextMonth() {
  if (!canGoNextMonth.value) {
    return;
  }

  slideDirection.value = "next";
  visibleMonth.value = visibleMonth.value.add(1, "month");
}

function confirmSelection() {
  if (!pendingDate.value || isCurrentSelection.value) {
    emit("close");
    return;
  }

  emit("select", pendingDate.value.id);
  emit("close");
}
</script>

<template>
  <div
    class="fixed inset-0 z-[80] flex select-none items-end justify-center bg-ink/50 px-3 py-0 sm:items-center sm:p-6"
    role="dialog"
    aria-modal="true"
    @click.self="emit('close')"
  >
    <section
      class="w-full max-w-[400px] rounded-t-[28px] border border-ink/5 bg-white px-4 pb-5 pt-5 shadow-widget sm:rounded-[30px] sm:px-6"
    >
      <header
        class="grid grid-cols-[36px_minmax(0,1fr)_36px] items-center gap-2"
      >
        <button
          class="grid h-9 w-9 cursor-pointer place-items-center rounded-full text-ink transition hover:bg-ink/5 disabled:cursor-default disabled:text-ink/15 disabled:hover:bg-transparent"
          type="button"
          :disabled="!canGoPreviousMonth"
          aria-label="Предыдущий месяц"
          @click="goToPreviousMonth"
        >
          <svg class="h-6 w-6" aria-hidden="true">
            <use href="#ticket-widget-icon-arrow-left" />
          </svg>
        </button>

        <div class="relative h-7 overflow-hidden">
          <Transition :name="monthTransitionName">
            <h2
              :key="visibleMonthKey"
              class="absolute inset-x-0 top-0 m-0 text-center text-xl font-semibold leading-7 text-ink"
            >
              {{ formatMonth(visibleMonth) }}
            </h2>
          </Transition>
        </div>

        <button
          class="grid h-9 w-9 cursor-pointer place-items-center rounded-full text-ink transition hover:bg-ink/5 disabled:cursor-default disabled:text-ink/15 disabled:hover:bg-transparent"
          type="button"
          :disabled="!canGoNextMonth"
          aria-label="Следующий месяц"
          @click="goToNextMonth"
        >
          <svg class="h-6 w-6 rotate-180" aria-hidden="true">
            <use href="#ticket-widget-icon-arrow-left" />
          </svg>
        </button>
      </header>

      <div class="mt-6 grid grid-cols-7 gap-x-1">
        <p
          v-for="weekday in weekdays"
          :key="weekday"
          class="m-0 text-center text-sm font-normal leading-4 text-ink/40"
          :class="weekday === 'СБ' || weekday === 'ВС' ? 'text-darkred' : ''"
        >
          {{ weekday }}
        </p>
      </div>

      <div class="relative mt-2 h-[304px] overflow-hidden">
        <Transition :name="monthTransitionName">
          <div
            :key="visibleMonthKey"
            class="absolute inset-0 grid grid-cols-7 gap-x-1 gap-y-2"
          >
            <div
              v-for="(cell, index) in calendarCells"
              :key="cell?.id ?? `empty-${index}`"
              class="flex h-[44px] items-start justify-center"
            >
              <button
                v-if="cell"
                class="grid min-h-[40px] w-full max-w-[44px] content-start justify-items-center rounded-lg px-1 py-1.5 text-center transition"
                :class="
                  isDateSelectable(cell.date)
                    ? pendingDateId === cell.id
                      ? 'cursor-pointer bg-brand/10 text-ink hover:bg-brand/15'
                      : 'cursor-pointer text-ink hover:bg-ink/5'
                    : 'cursor-default text-ink/35'
                "
                type="button"
                :disabled="!isDateSelectable(cell.date)"
                @click="selectDate(cell)"
              >
                <span
                  class="text-base font-semibold leading-[18px]"
                  :class="
                    isWeekendCell(cell) && isDateSelectable(cell.date)
                      ? 'text-darkred'
                      : ''
                  "
                >
                  {{ cell.day }}
                </span>
                <span
                  v-if="
                    isDateSelectable(cell.date) &&
                    cell.option?.price !== null &&
                    cell.option?.price !== undefined
                  "
                  class="mt-0.5 whitespace-nowrap text-[9px] font-normal leading-3 text-ink/40"
                >
                  {{ formatPrice(cell.option.price) }}
                </span>
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <button
        class="mx-auto mt-6 block h-11 w-full max-w-[280px] cursor-pointer rounded-full text-base font-semibold transition"
        :class="
          pendingDate && !isCurrentSelection
            ? 'bg-brand text-white hover:bg-brand/90'
            : 'bg-ink/10 text-ink hover:bg-ink/15'
        "
        type="button"
        @click="confirmSelection"
      >
        {{ actionLabel }}
      </button>
    </section>
  </div>
</template>
